import React, { useEffect, useState } from 'react';
import api from '../api/axios';
import { 
  FaSearch, FaTrash, FaEdit, FaAngleLeft, FaAngleRight, 
  FaAngleDoubleLeft, FaAngleDoubleRight, FaTimes, FaSave,
  FaUser, FaEnvelope, FaCalendar, FaShieldAlt, FaUsers,
  FaEye, FaEyeSlash, FaFilter, FaSort, FaSortUp, FaSortDown
} from 'react-icons/fa';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const UserList = () => {
  // Estados principales
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Estados de paginación
  const [currentPage, setCurrentPage] = useState(1);
  const [usersPerPage, setUsersPerPage] = useState(10);
  const [totalUsers, setTotalUsers] = useState(0);

  // Estados para edición y eliminación
  const [editingUser, setEditingUser] = useState(null);
  const [editForm, setEditForm] = useState({ name: '', email: '', role: '' });
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [isEditing, setIsEditing] = useState(false);

  // Estados para filtros y ordenamiento
  const [roleFilter, setRoleFilter] = useState('all');
  const [sortField, setSortField] = useState('createdAt');
  const [sortDirection, setSortDirection] = useState('desc');
  const [showFilters, setShowFilters] = useState(false);

  // Paleta de colores profesional
  const colors = {
    primary: '#0C4B45',
    primaryLight: '#83F4E9',
    primaryDark: '#083D38',
    secondary: '#662D8F',
    secondaryLight: '#F2A9FD',
    accent: '#4CAF50',
    warning: '#FF9800',
    danger: '#F44336',
    textDark: '#0C4B45',
    textLight: '#E0F3EB',
    background: '#F0F9F5'
  };

  // Obtener usuarios con paginación
  const fetchUsers = async () => {
    try {
      setLoading(true);
      const res = await api.get('/admin/users', { params: { page: currentPage, limit: usersPerPage } });

      const usersData = res.data?.data || [];
      const total = res.data?.pagination?.total || 0;

      if (Array.isArray(usersData)) {
        setUsers(usersData);
        setFilteredUsers(usersData);
        setTotalUsers(total);
      } else {
        setUsers([]);
        setFilteredUsers([]);
      }
    } catch (error) {
      console.error('❌ Error al obtener usuarios:', error);
      setError('Error al cargar los usuarios');
      toast.error('Error al cargar los usuarios', {
        position: "top-right",
        className: 'bg-white border-l-4 border-red-500 shadow-lg'
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, [currentPage, usersPerPage]);

  // Filtrar y ordenar usuarios
  useEffect(() => {
    let result = [...users];

    // Aplicar filtro de búsqueda
    if (searchTerm) {
      result = result.filter(user =>
        user.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.role.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Aplicar filtro de rol
    if (roleFilter !== 'all') {
      result = result.filter(user => user.role === roleFilter);
    }

    // Aplicar ordenamiento
    result.sort((a, b) => {
      let aValue = a[sortField];
      let bValue = b[sortField];

      if (sortField === 'createdAt') {
        aValue = new Date(aValue);
        bValue = new Date(bValue);
      }

      if (sortDirection === 'asc') {
        return aValue > bValue ? 1 : -1;
      } else {
        return aValue < bValue ? 1 : -1;
      }
    });

    setFilteredUsers(result);
  }, [searchTerm, users, roleFilter, sortField, sortDirection]);

  // Iniciar edición de usuario
  const startEdit = (user) => {
    setEditingUser(user);
    setEditForm({
      name: user.name,
      email: user.email,
      role: user.role
    });
    setIsEditing(true);
  };

  // Cancelar edición
  const cancelEdit = () => {
    setEditingUser(null);
    setEditForm({ name: '', email: '', role: '' });
    setIsEditing(false);
  };

  // Guardar cambios del usuario
  const saveEdit = async () => {
    try {
      const res = await api.patch(`/admin/users/${editingUser._id}/role`, { role: editForm.role });

      // Actualizar lista localmente
      const updatedUsers = users.map(user =>
        user._id === editingUser._id 
          ? { ...user, name: editForm.name, email: editForm.email, role: editForm.role }
          : user
      );

      setUsers(updatedUsers);
      setFilteredUsers(updatedUsers);
      cancelEdit();

      toast.success(
        <div className="flex items-center">
          <FaSave className="text-[#0C4B45] mr-2" />
          <span>Usuario actualizado correctamente</span>
        </div>,
        {
          position: "top-right",
          className: 'bg-white border-l-4 border-[#4CAF50] shadow-lg'
        }
      );
    } catch (error) {
      console.error('❌ Error al actualizar usuario:', error);
      toast.error('Error al actualizar usuario', {
        position: "top-right",
        className: 'bg-white border-l-4 border-red-500 shadow-lg'
      });
    }
  };

  // Confirmar eliminación
  const confirmDelete = (user) => {
    setDeleteConfirm(user);
  };

  // Cancelar eliminación
  const cancelDelete = () => {
    setDeleteConfirm(null);
  };

  // Eliminar usuario confirmado
  const executeDelete = async () => {
    try {
      await api.delete(`/admin/users/${deleteConfirm._id}`);

      // Actualizar lista después de eliminar
      const updatedUsers = users.filter(user => user._id !== deleteConfirm._id);
      setUsers(updatedUsers);
      setFilteredUsers(updatedUsers);
      setTotalUsers(prev => prev - 1);
      setDeleteConfirm(null);

      toast.success(
        <div className="flex items-center">
          <FaTrash className="text-[#0C4B45] mr-2" />
          <span>Usuario eliminado correctamente</span>
        </div>,
        {
          position: "top-right",
          className: 'bg-white border-l-4 border-[#662D8F] shadow-lg'
        }
      );

      // Si la página queda vacía y no es la primera, retroceder
      if (updatedUsers.length === 0 && currentPage > 1) {
        setCurrentPage(currentPage - 1);
      }
    } catch (error) {
      console.error('❌ Error al eliminar usuario:', error);
      toast.error('Error al eliminar usuario', {
        position: "top-right",
        className: 'bg-white border-l-4 border-red-500 shadow-lg'
      });
    }
  };

  // Manejar ordenamiento
  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  // Lógica de paginación
  const totalPages = Math.ceil(totalUsers / usersPerPage);
  const maxVisiblePages = 5;

  const getPaginationRange = () => {
    const range = [];
    const half = Math.floor(maxVisiblePages / 2);
    let start = Math.max(1, currentPage - half);
    let end = Math.min(totalPages, start + maxVisiblePages - 1);

    if (end - start + 1 < maxVisiblePages) {
      start = Math.max(1, end - maxVisiblePages + 1);
    }

    for (let i = start; i <= end; i++) {
      range.push(i);
    }

    return range;
  };

  const paginationRange = getPaginationRange();

  const handlePageChange = (page) => {
    if (page < 1 || page > totalPages) return;
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Estados de carga mejorados
  if (loading) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-gradient-to-br from-[#0C4B45]/5 via-white to-[#662D8F]/5 p-6">
        <motion.div
          animate={{ 
            rotate: 360,
            scale: [1, 1.1, 1]
          }}
          transition={{ 
            rotate: { duration: 2, repeat: Infinity, ease: "linear" },
            scale: { duration: 1.5, repeat: Infinity }
          }}
          className="relative"
        >
          <div className="w-20 h-20 border-4 border-[#662D8F] border-t-transparent rounded-full" />
          <motion.div
            animate={{ rotate: -360 }}
            transition={{ duration: 1.5, repeat: Infinity, ease: "linear" }}
            className="absolute inset-0"
          >
            <FaUsers className="text-[#0C4B45] w-8 h-8 absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2" />
          </motion.div>
        </motion.div>
        <motion.p 
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="mt-6 text-xl font-semibold text-[#0C4B45] bg-white/80 px-6 py-3 rounded-2xl shadow-lg"
        >
          Cargando lista de usuarios...
        </motion.p>
      </div>
    );
  }

  // Manejo de errores mejorado
  if (error) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-gradient-to-br from-[#0C4B45]/5 via-white to-[#662D8F]/5 p-6">
        <motion.div 
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          className="bg-white/80 backdrop-blur-sm rounded-3xl shadow-2xl p-8 max-w-md w-full text-center border border-white/20"
        >
          <motion.div
            animate={{ 
              shake: { rotate: [-5, 5, -5, 5, 0] },
              scale: [1, 1.05, 1]
            }}
            transition={{ duration: 0.6 }}
            className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4"
          >
            <svg className="w-8 h-8 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
            </svg>
          </motion.div>
          <h2 className="text-2xl font-bold text-[#0C4B45] mb-3">Error al cargar usuarios</h2>
          <p className="text-gray-600 mb-6 leading-relaxed">{error}</p>
          <motion.button
            onClick={() => window.location.reload()}
            className="w-full py-3 px-6 bg-gradient-to-r from-[#662D8F] to-[#512577] text-white rounded-2xl font-semibold hover:from-[#512577] hover:to-[#662D8F] transition-all shadow-lg"
            whileHover={{ scale: 1.02, boxShadow: "0 10px 25px -5px rgba(102, 45, 143, 0.4)" }}
            whileTap={{ scale: 0.98 }}
          >
            Reintentar
          </motion.button>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen w-full max-w-full overflow-x-hidden bg-gradient-to-br from-[#0C4B45]/5 via-white to-[#662D8F]/5 p-3 sm:p-4 md:p-6">
      {/* Modal de confirmación de eliminación mejorado */}
      <AnimatePresence>
        {deleteConfirm && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4"
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0, y: 20 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.9, opacity: 0, y: 20 }}
              className="bg-white rounded-3xl p-8 max-w-md w-full shadow-2xl border border-white/20"
            >
              <div className="text-center mb-6">
                <motion.div
                  animate={{ scale: [1, 1.1, 1] }}
                  transition={{ duration: 0.5 }}
                  className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4"
                >
                  <FaTrash className="text-2xl text-red-500" />
                </motion.div>
                <h3 className="text-2xl font-bold text-[#0C4B45] mb-2">Confirmar Eliminación</h3>
                <p className="text-gray-600">
                  ¿Estás seguro de eliminar al usuario <span className="font-semibold text-[#662D8F]">{deleteConfirm.name}</span>?
                </p>
                <p className="text-sm text-gray-500 mt-2">Esta acción no se puede deshacer</p>
              </div>
              <div className="flex space-x-4">
                <motion.button
                  onClick={cancelDelete}
                  className="flex-1 py-3 px-4 border-2 border-gray-300 text-gray-700 rounded-2xl hover:bg-gray-50 transition-all font-semibold"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  Cancelar
                </motion.button>
                <motion.button
                  onClick={executeDelete}
                  className="flex-1 py-3 px-4 bg-gradient-to-r from-red-500 to-red-600 text-white rounded-2xl hover:from-red-600 hover:to-red-700 transition-all font-semibold shadow-lg"
                  whileHover={{ scale: 1.02, boxShadow: "0 8px 20px -5px rgba(239, 68, 68, 0.4)" }}
                  whileTap={{ scale: 0.98 }}
                >
                  Eliminar
                </motion.button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <div className="mx-auto w-full min-w-0 max-w-7xl">
        {/* Header Mejorado */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white/80 backdrop-blur-sm rounded-3xl shadow-xl p-4 sm:p-6 md:p-8 mb-6 md:mb-8 border border-white/20"
        >
          <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-6">
            <div className="flex-1">
              <div className="flex items-center gap-3 mb-3">
                <div className="p-2.5 bg-gradient-to-r from-[#0C4B45] to-[#083D38] rounded-2xl shadow-lg">
                  <FaUsers className="text-white text-xl" />
                </div>
                <div>
                  <h1 className="text-2xl md:text-4xl font-bold text-[#0C4B45]">Gestión de Usuarios</h1>
                  <p className="text-gray-600 text-sm md:text-base mt-1">
                    Administra y organiza los usuarios registrados en el sistema
                  </p>
                </div>
              </div>
            </div>
            
            {/* Estadísticas */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 w-full lg:w-auto">
              <div className="bg-white rounded-2xl p-4 shadow-lg border border-gray-100">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600">Total</p>
                    <p className="text-2xl font-bold text-[#0C4B45]">{totalUsers}</p>
                  </div>
                  <div className="p-2 bg-[#0C4B45]/10 rounded-xl">
                    <FaUsers className="text-[#0C4B45]" />
                  </div>
                </div>
              </div>
              <div className="bg-white rounded-2xl p-4 shadow-lg border border-gray-100">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600">Administradores</p>
                    <p className="text-2xl font-bold text-[#662D8F]">
                      {users.filter(u => u.role === 'admin').length}
                    </p>
                  </div>
                  <div className="p-2 bg-[#662D8F]/10 rounded-xl">
                    <FaShieldAlt className="text-[#662D8F]" />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Barra de Herramientas Mejorada */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-lg p-4 sm:p-6 mb-6 md:mb-8 border border-white/20"
        >
          <div className="flex flex-col lg:flex-row gap-4 items-start lg:items-center justify-between">
            {/* Búsqueda */}
            <div className="flex-1 w-full lg:max-w-md">
              <div className="relative">
                <FaSearch className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  placeholder="Buscar usuarios por nombre, email o rol..."
                  className="w-full pl-12 pr-4 py-3 bg-white border border-gray-200 rounded-2xl focus:outline-none focus:ring-2 focus:ring-[#0C4B45]/20 focus:border-[#0C4B45] transition-all shadow-sm"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
            </div>

            {/* Controles */}
            <div className="grid w-full grid-cols-1 gap-3 sm:flex sm:w-auto sm:flex-wrap sm:items-center">
              {/* Filtro de Rol */}
              <div className="flex items-center gap-2 bg-white border border-gray-200 rounded-2xl px-4 py-2 shadow-sm">
                <FaFilter className="text-gray-400" />
                <select
                  value={roleFilter}
                  onChange={(e) => setRoleFilter(e.target.value)}
                  className="bg-transparent border-none focus:outline-none text-sm font-medium text-gray-700"
                >
                  <option value="all">Todos los roles</option>
                  <option value="user">Usuarios</option>
                  <option value="admin">Administradores</option>
                </select>
              </div>

              {/* Items por página */}
              <div className="flex items-center gap-2 bg-white border border-gray-200 rounded-2xl px-4 py-2 shadow-sm">
                <span className="text-sm text-gray-600 whitespace-nowrap">Mostrar:</span>
                <select
                  value={usersPerPage}
                  onChange={(e) => setUsersPerPage(Number(e.target.value))}
                  className="bg-transparent border-none focus:outline-none text-sm font-medium text-gray-700"
                >
                  <option value={5}>5</option>
                  <option value={10}>10</option>
                  <option value={20}>20</option>
                  <option value={50}>50</option>
                </select>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Tabla de Usuarios Mejorada */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2 }}
          className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-xl overflow-hidden border border-white/20"
        >
          <div className="divide-y divide-gray-100 xl:hidden">
            {filteredUsers.length > 0 ? filteredUsers.map((user) => (
              <div key={user._id} className="max-w-full overflow-hidden p-4">
                <div className="flex items-start gap-3">
                  <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-gradient-to-r from-[#662D8F] to-[#F2A9FD] font-bold text-white shadow-sm">
                    {user.name?.charAt(0)?.toUpperCase() || 'U'}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0"><p className="truncate font-semibold text-gray-900">{user.name}</p><p className="truncate text-sm text-gray-500">{user.email}</p></div>
                      <span className={`shrink-0 rounded-full border px-2 py-1 text-xs font-semibold ${user.role === 'admin' ? 'bg-[#F2A9FD] text-[#662D8F] border-[#F2A9FD]' : 'bg-[#83F4E9] text-[#0C4B45] border-[#83F4E9]'}`}>{user.role}</span>
                    </div>
                    <div className="mt-3 border-t border-gray-100 pt-3">
                      <span className="block truncate text-xs text-gray-500">Registro: {new Date(user.createdAt).toLocaleDateString('es-ES', { day: '2-digit', month: 'short', year: 'numeric' })}</span>
                      <div className="mt-3 grid w-full grid-cols-2 gap-2">
                        <button onClick={() => startEdit(user)} className="flex min-w-0 items-center justify-center gap-1.5 rounded-lg bg-[#0C4B45] px-2 py-2.5 text-sm font-semibold text-white shadow-sm" title="Editar usuario"><FaEdit className="shrink-0 text-sm" /> <span className="truncate">Editar</span></button>
                        <button onClick={() => confirmDelete(user)} className="flex min-w-0 items-center justify-center gap-1.5 rounded-lg bg-red-500 px-2 py-2.5 text-sm font-semibold text-white shadow-sm" title="Eliminar usuario"><FaTrash className="shrink-0 text-sm" /> <span className="truncate">Eliminar</span></button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )) : (
              <div className="p-8 text-center"><FaSearch className="mx-auto mb-3 text-4xl text-gray-300" /><p className="font-semibold text-gray-600">No se encontraron usuarios</p><p className="mt-1 text-sm text-gray-500">Ajusta los filtros o la búsqueda.</p></div>
            )}
          </div>
          <div className="hidden overflow-x-auto xl:block">
            <table className="w-full min-w-[620px] divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  {[
                    { key: 'name', label: 'Nombre', icon: FaUser },
                    { key: 'email', label: 'Email', icon: FaEnvelope },
                    { key: 'role', label: 'Rol', icon: FaShieldAlt },
                    { key: 'createdAt', label: 'Fecha Registro', icon: FaCalendar }
                  ].map(({ key, label, icon: Icon }) => (
                    <th 
                      key={key}
                      scope="col" 
                      className={`px-3 py-4 text-left text-xs font-semibold text-[#0C4B45] uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors md:px-6 ${key === 'email' || key === 'createdAt' ? 'hidden md:table-cell' : ''}`}
                      onClick={() => handleSort(key)}
                    >
                      <div className="flex items-center gap-2">
                        <Icon className="text-[#0C4B45]/60" />
                        <span>{label}</span>
                        {sortField === key && (
                          <motion.div
                            initial={{ scale: 0 }}
                            animate={{ scale: 1 }}
                            className="text-[#662D8F]"
                          >
                            {sortDirection === 'asc' ? <FaSortUp /> : <FaSortDown />}
                          </motion.div>
                        )}
                      </div>
                    </th>
                  ))}
                  <th scope="col" className="px-3 py-4 text-right text-xs font-semibold text-[#0C4B45] uppercase tracking-wider md:px-6">
                    Acciones
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                <AnimatePresence>
                  {filteredUsers.length > 0 ? (
                    filteredUsers.map((user, index) => (
                      <motion.tr 
                        key={user._id}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -20 }}
                        transition={{ duration: 0.3, delay: index * 0.05 }}
                        className="hover:bg-gray-50/80 transition-colors group"
                      >
                        <td className="px-3 py-4 whitespace-nowrap md:px-6">
                          <div className="flex items-center">
                            <motion.div 
                              whileHover={{ scale: 1.1 }}
                              className="flex-shrink-0 h-12 w-12 rounded-2xl bg-gradient-to-r from-[#662D8F] to-[#F2A9FD] flex items-center justify-center text-white font-bold text-lg shadow-lg"
                            >
                              {user.name.charAt(0).toUpperCase()}
                            </motion.div>
                            <div className="ml-4">
                              <div className="text-sm font-semibold text-gray-900">{user.name}</div>
                            </div>
                          </div>
                        </td>
                        <td className="hidden px-6 py-4 whitespace-nowrap md:table-cell">
                          <div className="text-sm text-gray-900">{user.email}</div>
                        </td>
                        <td className="px-3 py-4 whitespace-nowrap md:px-6">
                          <motion.span 
                            whileHover={{ scale: 1.05 }}
                            className={`px-3 py-1 inline-flex text-xs leading-5 font-semibold rounded-full border ${
                              user.role === 'admin' 
                                ? 'bg-[#F2A9FD] text-[#662D8F] border-[#F2A9FD]' 
                                : 'bg-[#83F4E9] text-[#0C4B45] border-[#83F4E9]'
                            } shadow-sm`}
                          >
                            {user.role}
                          </motion.span>
                        </td>
                        <td className="hidden px-6 py-4 whitespace-nowrap text-sm text-gray-500 md:table-cell">
                          {new Date(user.createdAt).toLocaleDateString('es-ES', {
                            year: 'numeric',
                            month: 'short',
                            day: 'numeric'
                          })}
                        </td>
                        <td className="px-3 py-4 whitespace-nowrap text-right text-sm font-medium md:px-6">
                          <div className="flex justify-end space-x-2">
                            <motion.button
                              onClick={() => startEdit(user)}
                              className="p-2 bg-[#0C4B45] text-white rounded-xl hover:bg-[#083D38] transition-all shadow-md"
                              whileHover={{ scale: 1.1, boxShadow: "0 5px 15px -3px rgba(12, 75, 69, 0.3)" }}
                              whileTap={{ scale: 0.9 }}
                              title="Editar usuario"
                            >
                              <FaEdit className="text-sm" />
                            </motion.button>
                            <motion.button
                              onClick={() => confirmDelete(user)}
                              className="p-2 bg-red-500 text-white rounded-xl hover:bg-red-600 transition-all shadow-md"
                              whileHover={{ scale: 1.1, boxShadow: "0 5px 15px -3px rgba(239, 68, 68, 0.3)" }}
                              whileTap={{ scale: 0.9 }}
                              title="Eliminar usuario"
                            >
                              <FaTrash className="text-sm" />
                            </motion.button>
                          </div>
                        </td>
                      </motion.tr>
                    ))
                  ) : (
                    <motion.tr
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ duration: 0.5 }}
                    >
                      <td colSpan="5" className="px-6 py-12 text-center">
                        <motion.div
                          animate={{ 
                            y: [0, -10, 0],
                            rotate: [0, 5, -5, 0]
                          }}
                          transition={{ duration: 3, repeat: Infinity }}
                          className="text-gray-400 mb-4"
                        >
                          <FaSearch className="text-6xl mx-auto" />
                        </motion.div>
                        <h3 className="text-lg font-semibold text-gray-600 mb-2">No se encontraron usuarios</h3>
                        <p className="text-gray-500">Intenta ajustar los filtros o términos de búsqueda</p>
                      </td>
                    </motion.tr>
                  )}
                </AnimatePresence>
              </tbody>
            </table>
          </div>
      

          {/* Paginación Mejorada */}
          {totalPages > 1 && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="bg-white/80 backdrop-blur-sm px-6 py-4 border-t border-gray-200"
            >
              <div className="flex flex-col md:flex-row justify-between items-center gap-4">
                <div className="text-sm text-gray-600">
                  Mostrando <span className="font-semibold text-[#0C4B45]">
                    {(currentPage - 1) * usersPerPage + 1}-{Math.min(currentPage * usersPerPage, totalUsers)}
                  </span> de <span className="font-semibold text-[#0C4B45]">{totalUsers}</span> usuarios
                </div>
                
                <div className="flex items-center space-x-2">
                  <motion.button
                    onClick={() => handlePageChange(1)}
                    disabled={currentPage === 1}
                    className="p-2 rounded-xl disabled:opacity-30 disabled:cursor-not-allowed bg-gray-100 text-gray-600 hover:bg-[#0C4B45] hover:text-white transition-all"
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    <FaAngleDoubleLeft />
                  </motion.button>
                  <motion.button
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                    className="p-2 rounded-xl disabled:opacity-30 disabled:cursor-not-allowed bg-gray-100 text-gray-600 hover:bg-[#0C4B45] hover:text-white transition-all"
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    <FaAngleLeft />
                  </motion.button>

                  {paginationRange.map(page => (
                    <motion.button
                      key={page}
                      onClick={() => handlePageChange(page)}
                      className={`px-3 py-2 rounded-xl text-sm font-semibold transition-all ${
                        currentPage === page
                          ? 'bg-gradient-to-r from-[#0C4B45] to-[#083D38] text-white shadow-lg'
                          : 'bg-white text-gray-700 hover:bg-gray-50 border border-gray-200'
                      }`}
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                    >
                      {page}
                    </motion.button>
                  ))}

                  <motion.button
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    className="p-2 rounded-xl disabled:opacity-30 disabled:cursor-not-allowed bg-gray-100 text-gray-600 hover:bg-[#0C4B45] hover:text-white transition-all"
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    <FaAngleRight />
                  </motion.button>
                  <motion.button
                    onClick={() => handlePageChange(totalPages)}
                    disabled={currentPage === totalPages}
                    className="p-2 rounded-xl disabled:opacity-30 disabled:cursor-not-allowed bg-gray-100 text-gray-600 hover:bg-[#0C4B45] hover:text-white transition-all"
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    <FaAngleDoubleRight />
                  </motion.button>
                </div>
              </div>
            </motion.div>
          )}
        </motion.div>

        {/* Modal de Edición Mejorado */}
        <AnimatePresence>
          {isEditing && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4"
            >
              <motion.div
                initial={{ scale: 0.9, opacity: 0, y: 20 }}
                animate={{ scale: 1, opacity: 1, y: 0 }}
                exit={{ scale: 0.9, opacity: 0, y: 20 }}
                className="bg-white rounded-3xl p-8 max-w-md w-full shadow-2xl border border-white/20"
              >
                <div className="flex justify-between items-center mb-6">
                  <h3 className="text-2xl font-bold text-[#0C4B45]">Editar Usuario</h3>
                  <motion.button
                    onClick={cancelEdit}
                    className="text-gray-400 hover:text-gray-600 transition-colors p-2 rounded-xl hover:bg-gray-100"
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.9 }}
                  >
                    <FaTimes className="text-lg" />
                  </motion.button>
                </div>

                <div className="space-y-6">
                  <div>
                    <label className="block text-sm font-semibold text-[#0C4B45] mb-3">
                      Nombre
                    </label>
                    <input
                      type="text"
                      value={editForm.name}
                      onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-2xl focus:outline-none focus:ring-2 focus:ring-[#662D8F] focus:border-transparent transition-all"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-[#0C4B45] mb-3">
                      Email
                    </label>
                    <input
                      type="email"
                      value={editForm.email}
                      onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-2xl focus:outline-none focus:ring-2 focus:ring-[#662D8F] focus:border-transparent transition-all"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-[#0C4B45] mb-3">
                      Rol
                    </label>
                    <select
                      value={editForm.role}
                      onChange={(e) => setEditForm({ ...editForm, role: e.target.value })}
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-2xl focus:outline-none focus:ring-2 focus:ring-[#662D8F] focus:border-transparent transition-all"
                    >
                      <option value="user">Usuario</option>
                      <option value="admin">Administrador</option>
                    </select>
                  </div>
                </div>

                <div className="flex space-x-4 mt-8">
                  <motion.button
                    onClick={cancelEdit}
                    className="flex-1 py-3 px-4 border-2 border-gray-300 text-gray-700 rounded-2xl hover:bg-gray-50 transition-all font-semibold"
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    Cancelar
                  </motion.button>
                  <motion.button
                    onClick={saveEdit}
                    className="flex-1 py-3 px-4 bg-gradient-to-r from-[#0C4B45] to-[#083D38] text-white rounded-2xl hover:from-[#083D38] hover:to-[#0C4B45] transition-all font-semibold shadow-lg flex items-center justify-center"
                    whileHover={{ scale: 1.02, boxShadow: "0 8px 20px -5px rgba(12, 75, 69, 0.4)" }}
                    whileTap={{ scale: 0.98 }}
                  >
                    <FaSave className="mr-2" />
                    Guardar
                  </motion.button>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default UserList;
